import type { IAppSettingsContext } from "@/application/ports/IAppSettingsContext";
import type { AppSettings, Language, Theme } from "@/domain/common";
import { useLogger } from "@/hooks/useLogger";
import { getAppSettingsStorageService } from "@/infrastructure/di";
import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";

const AppSettingsContext = createContext<IAppSettingsContext | undefined>(undefined);

const DEFAULT_SETTINGS: AppSettings = {
  theme: "system",
  language: "es",
};

export function AppSettingsProvider({ children }: { children: ReactNode }) {
  const { i18n } = useTranslation();
  const logger = useLogger();
  const appSettingsStorage = getAppSettingsStorageService();
  const [settings, setSettings] = useState<AppSettings>(() => {
    try {
      const stored = appSettingsStorage.getSettings();
      if (stored) {
        return { ...DEFAULT_SETTINGS, ...stored };
      }
    } catch (error) {
      logger.warn("Failed to load app settings from localStorage:", error);
    }
    return DEFAULT_SETTINGS;
  });

  const [isDarkMode, setIsDarkMode] = useState(false);

  // Function to determine if dark mode should be active
  const calculateIsDarkMode = (theme: Theme): boolean => {
    if (theme === "dark") return true;
    if (theme === "light") return false;
    // system theme
    return window.matchMedia("(prefers-color-scheme: dark)").matches;
  };

  // Update isDarkMode when theme changes or system preference changes
  useEffect(() => {
    const updateDarkMode = () => {
      const shouldBeDark = calculateIsDarkMode(settings.theme);
      setIsDarkMode(shouldBeDark);

      // Apply/remove dark class to document
      if (shouldBeDark) {
        document.documentElement.classList.add("dark");
      } else {
        document.documentElement.classList.remove("dark");
      }
    };

    updateDarkMode();

    // Listen for system theme changes when using 'system' theme
    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    const handleSystemThemeChange = () => {
      if (settings.theme === "system") {
        updateDarkMode();
      }
    };

    mediaQuery.addEventListener("change", handleSystemThemeChange);
    return () => mediaQuery.removeEventListener("change", handleSystemThemeChange);
  }, [settings.theme]);

  // Update language when it changes
  useEffect(() => {
    if (i18n.language !== settings.language) {
      i18n.changeLanguage(settings.language);
    }
  }, [settings.language, i18n]);

  // Save settings to localStorage whenever they change
  useEffect(() => {
    try {
      appSettingsStorage.saveSettings(settings);
    } catch (error) {
      logger.warn("Failed to save app settings to localStorage:", error);
    }
  }, [settings]);

  const setTheme = (theme: Theme) => {
    setSettings((prev) => ({ ...prev, theme }));
  };

  const setLanguage = (language: Language) => {
    setSettings((prev) => ({ ...prev, language }));
  };

  const value: IAppSettingsContext = {
    settings,
    setTheme,
    setLanguage,
    isDarkMode,
  };

  return <AppSettingsContext.Provider value={value}>{children}</AppSettingsContext.Provider>;
}

export function useAppSettings() {
  const context = useContext(AppSettingsContext);
  if (context === undefined) {
    throw new Error("useAppSettings must be used within an AppSettingsProvider");
  }
  return context;
}
